import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactDOM from "react-dom";
import { recalculateRundownTimes, addTimes } from "../../../utils/timeUtils";
import { CustomDialog } from "../../common/components/CustomDialog";
import { RundownItem, Rundown } from "../../common/types/domain";
import { useProjects } from "../../common/contexts/ProjectContext";
import { socketService } from "../../../services/socket";
import { CustomCalendar } from "../components/CustomCalendar";
import { RundownItemRow } from "../components/RundownItemRow";
import { AddEventPanel } from "../components/AddEventPanel";

interface RundownEditorProps {
  /** When true, hides "Activate for Production" and "+ New Rundown" buttons. */
  readOnly?: boolean;
}

export const RundownEditor: React.FC<RundownEditorProps> = ({ readOnly = false }) => {
  const { t } = useTranslation();
  const { activeProject } = useProjects();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allRundowns, setAllRundowns] = useState<Rundown[]>([]);

  const selectedRundownIdFromUrl = searchParams.get("rundownId") || "";
  const [selectedRundownId, setSelectedRundownIdState] = useState<string>(selectedRundownIdFromUrl);

  const setSelectedRundownId = (id: string, updateUrl: boolean = true) => {
    setSelectedRundownIdState(id);
    if (updateUrl) {
      if (id) {
        setSearchParams({ tab: "rundowns", rundownId: id });
      } else {
        setSearchParams({ tab: "rundowns" });
      }
    }
  };

  const [rundown, setRundown] = useState<RundownItem[]>([]);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingNameValue, setEditingNameValue] = useState("");

  // UI State
  const [viewMode, setViewMode] = useState<"list" | "events">(
    selectedRundownIdFromUrl ? "events" : "list",
  );
  // Layout of the "All Rundowns" screen: card grid vs compact list. Persisted.
  const [listLayout, setListLayout] = useState<"grid" | "list">(
    () => (localStorage.getItem("rundownListLayout") as "grid" | "list") || "grid",
  );
  const changeListLayout = (mode: "grid" | "list") => {
    setListLayout(mode);
    localStorage.setItem("rundownListLayout", mode);
  };
  const [isCreating, setIsCreating] = useState(false);
  const [newRundownName, setNewRundownName] = useState("");
  const [newRundownDate, setNewRundownDate] = useState(new Date().toISOString().split("T")[0]);
  const [showDaySelector, setShowDaySelector] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const daySelectorRef = useRef<HTMLDivElement>(null);
  // Inline date editing in dropdown
  const [editingDateId, setEditingDateId] = useState<string | null>(null);
  const editDateCalendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setShowCalendar(false);
      }
      // Check if click is inside the date-edit portal calendar
      const portalCalendar = document.getElementById("date-edit-portal");
      const isInsidePortal = portalCalendar?.contains(event.target as Node);

      if (
        daySelectorRef.current &&
        !daySelectorRef.current.contains(event.target as Node) &&
        !isInsidePortal
      ) {
        setShowDaySelector(false);
        setEditingDateId(null);
      }
      if (
        editDateCalendarRef.current &&
        !editDateCalendarRef.current.contains(event.target as Node) &&
        !isInsidePortal
      ) {
        setEditingDateId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Editing Item State
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    duration: "",
    plannedTime: "",
    isParallel: false,
  });

  // Drag & Drop State
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "alert" | "prompt" | "confirm";
    onConfirm: (val?: string) => void;
    defaultValue?: string;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "alert",
    onConfirm: () => {},
  });

  const selectedRundownIdRef = useRef(selectedRundownId);
  useEffect(() => {
    selectedRundownIdRef.current = selectedRundownId;
  }, [selectedRundownId]);

  // Track recently created rundown to prevent race conditions
  const pendingCreatedRundown = useRef<Rundown | null>(null);

  const [systemActiveId, setSystemActiveId] = useState<string>("");

  const [isLoading, setIsLoading] = useState(true);

  // --- SOCKET LISTENERS (using shared socketService) ---
  useEffect(() => {
    const handleInitData = (data: any) => {
      if (data.rundowns) {
        setIsLoading(false);
        let rundowns = data.rundowns as Rundown[];
        // If we just created a rundown, merge it into the list
        const pending = pendingCreatedRundown.current;
        if (pending && !rundowns.find((r: Rundown) => r.id === pending.id)) {
          rundowns = [...rundowns, pending];
        }
        setAllRundowns(rundowns);
        setSystemActiveId(data.activeRundownId);
        // Restore from DB — activeRundownId is the strict source of truth.
        // If null (not set or rundown deleted), clear selection — never auto-select.
        if (pending && rundowns.find((r: Rundown) => r.id === pending.id)) {
          setSelectedRundownId(pending.id);
          setRundown(pending.items || []);
        } else if (
          selectedRundownIdFromUrl &&
          rundowns.find((r: Rundown) => r.id === selectedRundownIdFromUrl)
        ) {
          setSelectedRundownId(selectedRundownIdFromUrl);
          const startData = rundowns.find((r: any) => r.id === selectedRundownIdFromUrl);
          if (startData) {
            setRundown(startData.items || []);
            setViewMode("events");
          }
        } else if (data.activeRundownId) {
          // DB has an explicit active rundown — restore it
          setSelectedRundownId(data.activeRundownId);
          const startData = rundowns.find((r: any) => r.id === data.activeRundownId);
          if (startData) {
            setRundown(startData.items || []);
            setViewMode("events");
          }
        } else {
          // No active rundown in DB — show empty state, never auto-select
          setSelectedRundownId("");
          setRundown([]);
        }
      }
    };

    const handleRundownsListUpdated = (list: Rundown[]) => {
      let finalList = list;
      // If we just created a rundown, merge it into the list
      const pending = pendingCreatedRundown.current;
      if (pending && !list.find((r) => r.id === pending.id)) {
        finalList = [...list, pending];
      } else if (pending && list.find((r) => r.id === pending.id)) {
        // The pending rundown is now in the list, clear the flag
        pendingCreatedRundown.current = null;
      }
      setAllRundowns(finalList);

      const stillExists = finalList.find((r) => r.id === selectedRundownIdRef.current);
      if (!stillExists) {
        // The selected rundown was deleted or is no longer in the list.
        // DB is the source of truth — clear the selection, never auto-jump to first.
        setSelectedRundownId("", false);
        setRundown([]);
      }
    };

    const handleActiveRundownChanged = (data: { id: string; rundown: Rundown }) => {
      setSystemActiveId(data.id);
    };

    const handleRundownCreated = (newRundown: Rundown) => {
      // Store as pending to protect against race conditions with init-data
      pendingCreatedRundown.current = newRundown;
      setAllRundowns((prev) => {
        if (prev.find((r) => r.id === newRundown.id)) return prev;
        return [...prev, newRundown];
      });
      setSelectedRundownId(newRundown.id);
      setRundown(newRundown.items || []);
      setIsCreating(false);
      setNewRundownName("");
      setNewRundownDate(new Date().toISOString().split("T")[0]);
    };

    const handleCreateError = (err: { message: string }) => {
      setDialog({
        isOpen: true,
        title: t("controller.rundownEditor.creationError"),
        message: t("controller.rundownEditor.failedCreate") + " " + err.message,
        type: "alert",
        onConfirm: () => setDialog((d) => ({ ...d, isOpen: false })),
      });
    };

    const handleDeleteError = (err: { message: string }) => {
      setDialog({
        isOpen: true,
        title: t("controller.rundownEditor.deletionError"),
        message: err.message,
        type: "alert",
        onConfirm: () => setDialog((d) => ({ ...d, isOpen: false })),
      });
    };

    const handleDuplicateError = (err: { message: string }) => {
      setDialog({
        isOpen: true,
        title: t("controller.rundownEditor.duplicationError", "Duplication Error"),
        message: err.message,
        type: "alert",
        onConfirm: () => setDialog((d) => ({ ...d, isOpen: false })),
      });
    };

    const handleRundownUpdated = (data: { id: string; items: RundownItem[] }) => {
      if (selectedRundownIdRef.current === data.id) {
        setRundown(data.items);
      }
    };

    // A rundown was cloned by another client: add the card to the list without
    // touching this client's current selection.
    const handleRundownCardAdded = (newRundown: Rundown) => {
      setAllRundowns((prev) =>
        prev.find((r) => r.id === newRundown.id) ? prev : [...prev, newRundown],
      );
    };

    const handleRundownRenamed = (data: { id: string; name: string }) => {
      setAllRundowns((prev) => prev.map((r) => (r.id === data.id ? { ...r, name: data.name } : r)));
    };

    // Register listeners FIRST
    socketService.on("init-data", handleInitData);
    socketService.on("rundowns-list-updated", handleRundownsListUpdated);
    socketService.on("active-rundown-changed", handleActiveRundownChanged);
    socketService.on("rundown-created", handleRundownCreated);
    socketService.on("create-rundown-error", handleCreateError);
    socketService.on("delete-rundown-error", handleDeleteError);
    socketService.on("duplicate-rundown-error", handleDuplicateError);
    socketService.on("rundown-updated", handleRundownUpdated);
    socketService.on("rundown-card-added", handleRundownCardAdded);
    socketService.on("rundown-renamed", handleRundownRenamed);

    // THEN emit — server accepts projectId as argument directly,
    // so no need to wait for select-project.
    if (activeProject) {
      // Lite payload: RundownEditor only needs rundowns + activeRundownId. Avoids
      // the heavy full-project fetch (stories, participants, tags, transcripts).
      socketService.emit("get-init-data", activeProject.id, null, { lite: true });
    }

    return () => {
      socketService.off("init-data", handleInitData);
      socketService.off("rundowns-list-updated", handleRundownsListUpdated);
      socketService.off("active-rundown-changed", handleActiveRundownChanged);
      socketService.off("rundown-created", handleRundownCreated);
      socketService.off("create-rundown-error", handleCreateError);
      socketService.off("delete-rundown-error", handleDeleteError);
      socketService.off("duplicate-rundown-error", handleDuplicateError);
      socketService.off("rundown-updated", handleRundownUpdated);
      socketService.off("rundown-card-added", handleRundownCardAdded);
      socketService.off("rundown-renamed", handleRundownRenamed);
    };
  }, [activeProject]);

  const updateRundown = (newItems: RundownItem[]) => {
    setRundown(newItems);
    socketService.emit("update-rundown", { id: selectedRundownId, items: newItems });
  };

  const handleRename = () => {
    const trimmed = editingNameValue.trim();
    if (!trimmed || !selectedRundownId) {
      setIsEditingName(false);
      return;
    }
    setAllRundowns((prev) =>
      prev.map((r) => (r.id === selectedRundownId ? { ...r, name: trimmed } : r)),
    );
    socketService.emit("rename-rundown", { id: selectedRundownId, name: trimmed });
    setIsEditingName(false);
  };

  const handleRundownSelect = (id: string) => {
    setSelectedRundownId(id);
    setShowDaySelector(false);
    setViewMode("events");
    const selectedRundown = allRundowns.find((r) => r.id === id);
    if (selectedRundown) {
      setRundown(selectedRundown.items || []);
    }
  };

  const handleActivate = () => {
    const targetRundown = allRundowns.find((r) => r.id === selectedRundownId);
    setDialog({
      isOpen: true,
      title: t("controller.rundownEditor.activateRundown"),
      message: t("controller.rundownEditor.activateRundownMsg", {
        name: targetRundown?.name || "this rundown",
      }),
      type: "confirm",
      onConfirm: () => {
        socketService.emit("select-rundown", selectedRundownId);
        setDialog((d) => ({ ...d, isOpen: false }));
      },
    });
  };

  const handleCreateRundown = () => {
    if (!newRundownName.trim()) {
      setDialog({
        isOpen: true,
        title: t("controller.rundownEditor.missingInfo"),
        message: t("controller.rundownEditor.enterName"),
        type: "alert",
        onConfirm: () => setDialog((d) => ({ ...d, isOpen: false })),
      });
      return;
    }

    if (!socketService.connected) {
      setDialog({
        isOpen: true,
        title: t("controller.rundownEditor.connectionLost"),
        message: t("controller.rundownEditor.serverDisconnected"),
        type: "alert",
        onConfirm: () => setDialog((d) => ({ ...d, isOpen: false })),
      });
      return;
    }

    socketService.emit("create-rundown", {
      name: newRundownName.trim().toUpperCase(),
      date: newRundownDate || new Date().toISOString().split("T")[0],
      projectId: activeProject?.id,
    });

    setViewMode("events");
    setNewRundownName("");
  };

  const handleUpdateDate = (rundownId: string, newDate: string) => {
    socketService.emit("update-rundown-metadata", { id: rundownId, date: newDate });
    // Optimistic local update
    setAllRundowns((prev) => prev.map((r) => (r.id === rundownId ? { ...r, date: newDate } : r)));
    setEditingDateId(null);
  };

  const handleDeleteRundown = (id: string, name: string) => {
    setDialog({
      isOpen: true,
      title: t("controller.rundownEditor.deleteRundown"),
      message: t("controller.rundownEditor.deleteRundownMsg", { name }),
      type: "confirm",
      onConfirm: () => {
        if (socketService.connected) {
          socketService.emit("delete-rundown", id);
        } else {
          setDialog({
            isOpen: true,
            title: t("controller.rundownEditor.connectionError"),
            message: t("controller.rundownEditor.noConnection"),
            type: "alert",
            onConfirm: () => setDialog((d) => ({ ...d, isOpen: false })),
          });
        }
        setDialog((d) => ({ ...d, isOpen: false }));
      },
    });
  };

  const handleDuplicateRundown = (id: string, name: string) => {
    setDialog({
      isOpen: true,
      title: t("controller.rundownEditor.duplicateRundown", "DUPLICATE RUNDOWN"),
      message: t("controller.rundownEditor.duplicateRundownMsg", {
        name,
        defaultValue: `Create a copy of "${name}" with all its events?`,
      }),
      type: "confirm",
      onConfirm: () => {
        if (socketService.connected) {
          socketService.emit("duplicate-rundown", id);
        } else {
          setDialog({
            isOpen: true,
            title: t("controller.rundownEditor.connectionError"),
            message: t("controller.rundownEditor.noConnection"),
            type: "alert",
            onConfirm: () => setDialog((d) => ({ ...d, isOpen: false })),
          });
        }
        setDialog((d) => ({ ...d, isOpen: false }));
      },
    });
  };

  const handleDragStart = (index: number) => {
    setDraggedItemIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (targetIndex: number) => {
    if (draggedItemIndex === null || draggedItemIndex === targetIndex) return;

    const newRundown = [...rundown];
    const [movedItem] = newRundown.splice(draggedItemIndex, 1);
    newRundown.splice(targetIndex, 0, movedItem);

    const recalculated = recalculateRundownTimes(newRundown);
    updateRundown(recalculated);
    setDraggedItemIndex(null);
  };

  const handleDelete = (id: string) => {
    const filteredList = rundown.filter((i) => i.id !== id);
    const recalculatedList = recalculateRundownTimes(filteredList);
    updateRundown(recalculatedList);
  };

  const startEditing = (item: RundownItem) => {
    setEditingId(item.id);
    setEditForm({
      title: item.title,
      description: item.description || "",
      duration: item.duration,
      plannedTime: item.plannedTime || "00:00:00",
      isParallel: !!item.isParallel,
    });
  };

  const saveEdit = () => {
    if (!editingId) return;

    const durationParts = editForm.duration.split(":").map(Number);
    const durationSeconds =
      (durationParts[0] || 0) * 3600 + (durationParts[1] || 0) * 60 + (durationParts[2] || 0);
    const finalDuration = durationSeconds < 600 ? "00:10:00" : editForm.duration;

    let newRundownList = rundown.map((item) =>
      item.id === editingId ? { ...item, ...editForm, duration: finalDuration } : item,
    );

    newRundownList = recalculateRundownTimes(newRundownList);
    updateRundown(newRundownList);
    setEditingId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  if (isLoading) {
    return (
      <div
        style={{
          background: "var(--color-panel-bg)",
          borderRadius: "var(--border-radius-lg)",
          boxShadow: "var(--shadow-card)",
          border: "1px solid var(--color-panel-border)",
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
          color: "var(--color-text-secondary)",
        }}
      >
        <div
          style={{
            width: "36px",
            height: "36px",
            border: "3px solid var(--color-panel-border)",
            borderTop: "3px solid var(--color-data-rundown)",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <span style={{ fontSize: "0.85rem", letterSpacing: "1px" }}>
          {t("controller.rundownEditor.loading", "LOADING RUNDOWNS...")}
        </span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (viewMode === "list") {
    return (
      <div
        data-testid="rundown-editor-list"
        style={{
          background: "var(--color-panel-bg)",
          borderRadius: "var(--border-radius-lg)",
          boxShadow: "var(--shadow-card)",
          border: "1px solid var(--color-panel-border)",
          width: "100%",
          color: "var(--color-text-primary)",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "var(--spacing-lg)",
            borderBottom: "1px solid var(--color-panel-border)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <div>
              <h2
                style={{
                  color: "var(--color-text-muted)",
                  margin: 0,
                  fontSize: "0.9rem",
                  letterSpacing: "2px",
                }}
              >
                {t("controller.rundownEditor.title")}
              </h2>
              <h1
                style={{
                  color: "var(--color-data-rundown)",
                  margin: "5px 0 0 0",
                  fontSize: "1.8rem",
                }}
              >
                All Rundowns{" "}
                <span
                  style={{
                    fontSize: "0.7rem",
                    color: "var(--color-text-muted)",
                    marginLeft: "10px",
                    verticalAlign: "middle",
                  }}
                >
                  (TOTAL: {allRundowns.length})
                </span>
              </h1>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {/* Layout toggle: card grid vs compact list */}
              <div
                style={{
                  display: "flex",
                  background: "var(--color-input-bg)",
                  border: "1px solid var(--color-panel-border)",
                  borderRadius: "var(--border-radius-sm)",
                  overflow: "hidden",
                }}
              >
                {(["grid", "list"] as const).map((mode) => (
                  <button
                    key={mode}
                    data-testid={`rundown-layout-${mode}`}
                    onClick={() => changeListLayout(mode)}
                    title={
                      mode === "grid"
                        ? t("controller.rundownEditor.gridView", "Grid view")
                        : t("controller.rundownEditor.listView", "List view")
                    }
                    style={{
                      background: listLayout === mode ? "var(--color-data-rundown)" : "transparent",
                      color: listLayout === mode ? "#000" : "var(--color-text-muted)",
                      border: "none",
                      padding: "9px 12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "1.1rem",
                    }}
                  >
                    <i className={mode === "grid" ? "bx bx-grid-alt" : "bx bx-list-ul"} />
                  </button>
                ))}
              </div>
              <button
                onClick={() => setIsCreating(!isCreating)}
                style={{
                  background: isCreating
                    ? "var(--color-panel-border)"
                    : "var(--color-data-rundown)",
                  color: isCreating ? "rgba(255,255,255,0.8)" : "#000",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "var(--border-radius-sm)",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                {isCreating
                  ? t("controller.rundownEditor.cancel")
                  : t("controller.rundownEditor.newRundown")}
              </button>
            </div>
          </div>
          {isCreating && (
            <div
              data-testid="rundown-create-form"
              style={{
                background: "var(--color-bg-base)",
                border: "1px solid var(--color-panel-border)",
                padding: "var(--spacing-lg)",
                borderRadius: "var(--border-radius-md)",
                display: "flex",
                gap: "var(--spacing-lg)",
                alignItems: "flex-end",
              }}
            >
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.7rem",
                    color: "var(--color-text-secondary)",
                    marginBottom: "var(--spacing-xs)",
                    fontWeight: "bold",
                  }}
                >
                  {t("controller.rundownEditor.rundownName")}
                </label>
                <input
                  value={newRundownName}
                  onChange={(e) => setNewRundownName(e.target.value)}
                  placeholder={t("controller.rundownEditor.rundownNamePlaceholder") || ""}
                  onKeyDown={(e) => e.key === "Enter" && handleCreateRundown()}
                  style={{
                    width: "100%",
                    background: "var(--color-input-bg)",
                    border: "1px solid var(--color-panel-border)",
                    color: "var(--color-text-primary)",
                    padding: "12px var(--spacing-md)",
                    borderRadius: "var(--border-radius-sm)",
                    outline: "none",
                  }}
                />
              </div>
              <div style={{ position: "relative" }} ref={calendarRef}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.7rem",
                    color: "var(--color-text-secondary)",
                    marginBottom: "var(--spacing-xs)",
                    fontWeight: "bold",
                  }}
                >
                  {t("controller.rundownEditor.date")}
                </label>
                <div
                  onClick={() => setShowCalendar(!showCalendar)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: "var(--color-input-bg)",
                    border: "1px solid var(--color-panel-border)",
                    color: "var(--color-text-primary)",
                    padding: "12px 12px 12px 40px",
                    borderRadius: "var(--border-radius-sm)",
                    cursor: "pointer",
                    width: "160px",
                    fontSize: "0.9rem",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: "12px",
                      color: "var(--color-data-rundown)",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <i className="bx bx-calendar" />
                  </div>
                  {newRundownDate}
                </div>
                {showCalendar && (
                  <CustomCalendar
                    value={newRundownDate}
                    onChange={setNewRundownDate}
                    onClose={() => setShowCalendar(false)}
                  />
                )}
              </div>
              <button
                onClick={handleCreateRundown}
                style={{
                  background: "var(--color-data-rundown)",
                  color: "#000",
                  border: "none",
                  padding: "14px 28px",
                  borderRadius: "var(--border-radius-sm)",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                {t("controller.rundownEditor.createRundown")}
              </button>
            </div>
          )}
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "var(--spacing-lg)" }}>
          <div
            style={
              listLayout === "grid"
                ? {
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                    gap: "20px",
                  }
                : {
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }
            }
          >
            {allRundowns.map((r) => (
              <div
                key={r.id}
                onClick={() => handleRundownSelect(r.id)}
                style={{
                  background: "var(--color-bg-base)",
                  border: "1px solid var(--color-panel-border)",
                  borderRadius: "var(--border-radius-md)",
                  padding: listLayout === "grid" ? "20px" : "12px 16px",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  display: "flex",
                  flexDirection: listLayout === "grid" ? "column" : "row",
                  alignItems: listLayout === "grid" ? "stretch" : "center",
                  gap: listLayout === "grid" ? "10px" : "16px",
                  position: "relative",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--color-data-rundown)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--color-panel-border)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: listLayout === "grid" ? "space-between" : "flex-start",
                    alignItems: "center",
                    gap: "10px",
                    flex: listLayout === "grid" ? undefined : 1,
                    minWidth: 0,
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      color: "var(--color-text-primary)",
                      fontSize: listLayout === "grid" ? "1.2rem" : "1rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {r.name}
                  </h3>
                  {systemActiveId === r.id && (
                    <span
                      style={{
                        fontSize: "0.7rem",
                        color: "var(--color-accent-success)",
                        background: "rgba(0, 208, 132, 0.1)",
                        padding: "4px 8px",
                        borderRadius: "10px",
                        fontWeight: "bold",
                      }}
                    >
                      ● LIVE
                    </span>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "var(--color-text-secondary)",
                    fontSize: "0.9rem",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  <i className="bx bx-calendar" style={{ color: "var(--color-data-rundown)" }}></i>
                  {r.date || t("controller.rundownEditor.noDate")}
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: listLayout === "grid" ? "10px" : 0,
                    gap: "4px",
                  }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDuplicateRundown(r.id, r.name);
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "var(--color-text-muted)",
                      cursor: "pointer",
                      padding: "5px",
                      borderRadius: "5px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "var(--color-data-rundown)";
                      e.currentTarget.style.background = "rgba(255, 200, 0, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "var(--color-text-muted)";
                      e.currentTarget.style.background = "transparent";
                    }}
                    title={t("controller.rundownEditor.duplicateRundown", "Duplicate Rundown")}
                  >
                    <i className="bx bx-copy" style={{ fontSize: "1.2rem" }}></i>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteRundown(r.id, r.name);
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "var(--color-text-muted)",
                      cursor: "pointer",
                      padding: "5px",
                      borderRadius: "5px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "var(--color-accent-danger)";
                      e.currentTarget.style.background = "rgba(255, 0, 0, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "var(--color-text-muted)";
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <i className="bx bx-trash" style={{ fontSize: "1.2rem" }}></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <CustomDialog
          isOpen={dialog.isOpen}
          title={dialog.title}
          message={dialog.message}
          type={dialog.type}
          onConfirm={dialog.onConfirm}
          onCancel={() => setDialog((d) => ({ ...d, isOpen: false }))}
          defaultValue={dialog.defaultValue}
        />
      </div>
    );
  }

  return (
    <div
      data-testid="rundown-editor"
      style={{
        background: "var(--color-panel-bg)",
        borderRadius: "var(--border-radius-lg)",
        boxShadow: "var(--shadow-card)",
        border: "1px solid var(--color-panel-border)",
        width: "100%",
        color: "var(--color-text-primary)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* TOP SECTION: Header & Creation Form */}
      <div
        style={{
          padding: "var(--spacing-lg) var(--spacing-lg) 0 var(--spacing-lg)",
          borderBottom: "1px solid var(--color-panel-border)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            <h2
              data-testid="rundown-title"
              style={{
                color: "var(--color-text-muted)",
                margin: 0,
                fontSize: "0.9rem",
                letterSpacing: "2px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <button
                onClick={() => {
                  setViewMode("list");
                  setSearchParams({ tab: "rundowns" });
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--color-data-rundown)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: 0,
                  fontSize: "0.9rem",
                  fontWeight: "bold",
                }}
                title={t("controller.rundownEditor.backToList", "Back to list")}
              >
                <i className="bx bx-arrow-back" />{" "}
                {t("controller.rundownEditor.backToList", "Back")}
              </button>
              | {t("controller.rundownEditor.title")}
            </h2>
            <h1
              data-testid="rundown-name"
              style={{
                color: "var(--color-data-rundown)",
                margin: "5px 0 0 0",
                fontSize: "1.8rem",
                textShadow: "0 0 10px rgba(245, 158, 11, 0.3)",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              {isEditingName ? (
                <input
                  data-testid="rundown-rename-input"
                  autoFocus
                  value={editingNameValue}
                  onChange={(e) => setEditingNameValue(e.target.value.toUpperCase())}
                  onBlur={handleRename}
                  onKeyDown={(e) => e.key === "Enter" && handleRename()}
                  style={{
                    background: "var(--color-input-bg)",
                    border: "1px solid var(--color-panel-border)",
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: "1.8rem",
                    fontWeight: "bold",
                    padding: "4px 10px",
                    borderRadius: "8px",
                    outline: "none",
                    width: "400px",
                  }}
                />
              ) : (
                <>
                  {allRundowns.find((r) => r.id === selectedRundownId)?.name ||
                    t("controller.rundownEditor.noRundownSelected")}
                  <button
                    data-testid="rundown-rename-btn"
                    onClick={() => {
                      const current = allRundowns.find((r) => r.id === selectedRundownId);
                      if (current) {
                        setEditingNameValue(current.name);
                        setIsEditingName(true);
                      }
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "var(--color-text-muted)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      padding: "8px",
                      borderRadius: "50%",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.color = "var(--color-data-rundown)")
                    }
                    onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-text-muted)")}
                    title={t("controller.rundownEditor.renameRundown")}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                    </svg>
                  </button>
                </>
              )}
              <span
                style={{
                  fontSize: "0.7rem",
                  color: "var(--color-text-muted)",
                  marginLeft: "10px",
                  verticalAlign: "middle",
                  fontWeight: "normal",
                }}
              >
                (TOTAL: {allRundowns.length})
              </span>
            </h1>
            {/* Rundown date display + inline edit */}
            {selectedRundownId &&
              (() => {
                const currentRundown = allRundowns.find((r) => r.id === selectedRundownId);
                if (!currentRundown) return null;
                return (
                  <div
                    data-testid="rundown-date-display"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginTop: "4px",
                      position: "relative",
                    }}
                  >
                    <i
                      className="bx bx-calendar"
                      style={{ fontSize: "0.9rem", color: "var(--color-data-rundown)" }}
                    ></i>
                    <span
                      data-testid="rundown-date-value"
                      style={{
                        fontSize: "0.85rem",
                        color: "var(--color-text-secondary)",
                        fontWeight: "500",
                      }}
                    >
                      {currentRundown.date || t("controller.rundownEditor.noDate")}
                    </span>
                    <button
                      id="header-date-edit-btn"
                      data-testid="rundown-date-edit-btn"
                      onClick={() =>
                        setEditingDateId(
                          editingDateId === selectedRundownId ? null : selectedRundownId,
                        )
                      }
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "var(--color-text-muted)",
                        cursor: "pointer",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        transition: "all 0.2s",
                        display: "flex",
                        alignItems: "center",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = "var(--color-data-rundown)";
                        e.currentTarget.style.background = "rgba(245, 158, 11, 0.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = "var(--color-text-muted)";
                        e.currentTarget.style.background = "transparent";
                      }}
                      title={t("controller.rundownEditor.editDate")}
                    >
                      <i className="bx bx-pencil" style={{ fontSize: "0.85rem" }}></i>
                    </button>
                    {editingDateId === selectedRundownId &&
                      (() => {
                        const btn = document.getElementById("header-date-edit-btn");
                        const rect = btn?.getBoundingClientRect();
                        const calTop = (rect?.bottom ?? 100) + 8;
                        const calLeft = rect?.left ?? 100;
                        return ReactDOM.createPortal(
                          <div
                            id="date-edit-portal"
                            style={{
                              position: "fixed",
                              top: calTop,
                              left: calLeft,
                              zIndex: 99999,
                              background: "var(--color-bg-base)",
                              border: "1px solid var(--color-panel-border)",
                              padding: "15px",
                              borderRadius: "var(--border-radius-md)",
                              boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                              width: "280px",
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <CustomCalendar
                              value={currentRundown.date || ""}
                              onChange={(newDate) => {
                                handleUpdateDate(selectedRundownId, newDate);
                              }}
                              onClose={() => setEditingDateId(null)}
                              inline={true}
                            />
                          </div>,
                          document.body,
                        );
                      })()}
                  </div>
                );
              })()}
          </div>

          <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
            {!readOnly &&
              (selectedRundownId !== systemActiveId ? (
                <button
                  data-testid="rundown-activate-btn"
                  onClick={handleActivate}
                  style={{
                    background: "var(--color-accent-warning)",
                    color: "#000",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "var(--border-radius-sm)",
                    fontWeight: "bold",
                    cursor: "pointer",
                    boxShadow: "0 0 10px rgba(255, 180, 0, 0.3)",
                  }}
                >
                  {t("controller.rundownEditor.activateForProduction")}
                </button>
              ) : (
                <div
                  data-testid="rundown-live-indicator"
                  style={{
                    background: "rgba(0, 208, 132, 0.1)",
                    color: "var(--color-accent-success)",
                    padding: "8px 15px",
                    borderRadius: "var(--border-radius-sm)",
                    fontSize: "0.8rem",
                    fontWeight: "bold",
                    border: "1px solid var(--color-accent-success)",
                  }}
                >
                  {t("controller.rundownEditor.liveOnSystem")}
                </div>
              ))}

            <div style={{ position: "relative" }} ref={daySelectorRef}>
              <button
                data-testid="rundown-switch-day-btn"
                onClick={() => setShowDaySelector(!showDaySelector)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  background: "var(--color-input-bg)",
                  padding: "8px 15px",
                  borderRadius: "10px",
                  border: "1px solid var(--color-panel-border)",
                  color: "var(--color-text-primary)",
                  fontWeight: "bold",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                }}
              >
                <span style={{ color: "var(--color-text-muted)", fontSize: "0.7rem" }}>
                  {t("controller.rundownEditor.switchDay")}:
                </span>
                {allRundowns.find((r) => r.id === selectedRundownId)?.name ||
                  t("controller.rundownEditor.selectPlaceholder")}
                <span style={{ fontSize: "0.6rem", opacity: 0.5 }}>▼</span>
              </button>

              {showDaySelector && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    width: "320px",
                    background: "var(--color-bg-base)",
                    border: "1px solid var(--color-panel-border)",
                    borderRadius: "var(--border-radius-md)",
                    marginTop: "8px",
                    zIndex: 100,
                    boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                    padding: "8px",
                    maxHeight: "400px",
                    overflowY: "auto",
                  }}
                >
                  {allRundowns.map((r) => (
                    <div
                      key={r.id}
                      data-testid={`rundown-selector-item-${r.id}`}
                      style={{ position: "relative", marginBottom: "4px" }}
                    >
                      <div
                        onClick={() => handleRundownSelect(r.id)}
                        style={{
                          padding: "12px 15px",
                          borderRadius: "var(--border-radius-sm)",
                          cursor: "pointer",
                          color:
                            selectedRundownId === r.id
                              ? "var(--color-data-rundown)"
                              : "var(--color-text-secondary)",
                          background:
                            selectedRundownId === r.id ? "rgba(245, 158, 11, 0.1)" : "transparent",
                          transition: "all 0.2s",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            selectedRundownId === r.id ? "rgba(245, 158, 11, 0.1)" : "transparent";
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: "bold", fontSize: "0.85rem" }}>{r.name}</div>
                          <div
                            style={{
                              fontSize: "0.7rem",
                              opacity: 0.6,
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              marginTop: "2px",
                            }}
                          >
                            <span>{r.date || t("controller.rundownEditor.noDate")}</span>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                          {systemActiveId === r.id && (
                            <span
                              style={{
                                fontSize: "0.6rem",
                                color: "var(--color-accent-success)",
                                fontWeight: "bold",
                              }}
                            >
                              ● {t("controller.rundownEditor.liveIndicator")}
                            </span>
                          )}
                          <button
                            data-testid={`rundown-duplicate-btn-${r.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicateRundown(r.id, r.name);
                            }}
                            style={{
                              background: "rgba(255,255,255,0.05)",
                              border: "1px solid var(--color-panel-border)",
                              color: "var(--color-text-muted)",
                              padding: "6px",
                              borderRadius: "6px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = "var(--color-data-rundown)";
                              e.currentTarget.style.borderColor = "var(--color-data-rundown)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = "var(--color-text-muted)";
                              e.currentTarget.style.borderColor = "var(--color-panel-border)";
                            }}
                            title={t(
                              "controller.rundownEditor.duplicateRundown",
                              "Duplicate Rundown",
                            )}
                          >
                            <svg
                              width="13"
                              height="13"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                          </button>
                          <button
                            data-testid={`rundown-delete-btn-${r.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteRundown(r.id, r.name);
                            }}
                            style={{
                              background: "rgba(255,255,255,0.05)",
                              border: "1px solid var(--color-panel-border)",
                              color: "var(--color-text-muted)",
                              padding: "6px",
                              borderRadius: "6px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = "var(--color-accent-danger)";
                              e.currentTarget.style.borderColor = "var(--color-accent-danger)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = "var(--color-text-muted)";
                              e.currentTarget.style.borderColor = "var(--color-panel-border)";
                            }}
                          >
                            <svg
                              width="13"
                              height="13"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!readOnly && (
              <button
                data-testid="new-rundown-btn"
                onClick={() => setIsCreating(!isCreating)}
                style={{
                  background: isCreating
                    ? "var(--color-panel-border)"
                    : "var(--color-data-rundown)",
                  color: isCreating ? "rgba(255,255,255,0.8)" : "#000",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "var(--border-radius-sm)",
                  fontWeight: "bold",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                {isCreating
                  ? t("controller.rundownEditor.cancel")
                  : t("controller.rundownEditor.newRundown")}
              </button>
            )}
          </div>
        </div>

        {isCreating && (
          <div
            data-testid="rundown-create-form"
            style={{
              background: "var(--color-bg-base)",
              border: "1px solid var(--color-panel-border)",
              padding: "var(--spacing-lg)",
              borderRadius: "var(--border-radius-md)",
              marginBottom: "var(--spacing-lg)",
              display: "flex",
              gap: "var(--spacing-lg)",
              alignItems: "flex-end",
              boxShadow: "var(--shadow-card)",
            }}
          >
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  fontSize: "0.7rem",
                  color: "var(--color-text-secondary)",
                  marginBottom: "var(--spacing-xs)",
                  fontWeight: "bold",
                }}
              >
                {t("controller.rundownEditor.rundownName")}
              </label>
              <input
                data-testid="rundown-name-input"
                value={newRundownName}
                onChange={(e) => setNewRundownName(e.target.value)}
                placeholder={t("controller.rundownEditor.rundownNamePlaceholder")}
                onKeyDown={(e) => e.key === "Enter" && handleCreateRundown()}
                style={{
                  width: "100%",
                  background: "var(--color-input-bg)",
                  border: "1px solid var(--color-panel-border)",
                  color: "var(--color-text-primary)",
                  padding: "12px var(--spacing-md)",
                  borderRadius: "var(--border-radius-sm)",
                  outline: "none",
                }}
              />
            </div>
            <div style={{ position: "relative" }} ref={calendarRef}>
              <label
                style={{
                  display: "block",
                  fontSize: "0.7rem",
                  color: "var(--color-text-secondary)",
                  marginBottom: "var(--spacing-xs)",
                  fontWeight: "bold",
                }}
              >
                {t("controller.rundownEditor.date")}
              </label>
              <div
                onClick={() => setShowCalendar(!showCalendar)}
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  background: "var(--color-input-bg)",
                  border: "1px solid var(--color-panel-border)",
                  color: "var(--color-text-primary)",
                  padding: "12px 12px 12px 40px",
                  borderRadius: "var(--border-radius-sm)",
                  cursor: "pointer",
                  width: "160px",
                  fontSize: "0.9rem",
                }}
              >
                {newRundownDate}
                <div
                  style={{
                    position: "absolute",
                    left: "12px",
                    color: "var(--color-data-rundown)",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </div>
              </div>
              {showCalendar && (
                <CustomCalendar
                  value={newRundownDate}
                  onChange={setNewRundownDate}
                  onClose={() => setShowCalendar(false)}
                />
              )}
            </div>
            <button
              data-testid="rundown-create-btn"
              onClick={handleCreateRundown}
              style={{
                background: "var(--color-data-rundown)",
                color: "#000",
                border: "none",
                padding: "14px 28px",
                borderRadius: "var(--border-radius-sm)",
                fontWeight: "bold",
                cursor: "pointer",
                transition: "all 0.2s",
                boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
            >
              {t("controller.rundownEditor.createRundown")}
            </button>
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "50px 30px 1.2fr 1.5fr 100px 100px 140px",
            gap: "10px",
            padding: "10px 15px",
            color: "var(--color-text-muted)",
            fontSize: "0.8rem",
            fontWeight: "bold",
          }}
        >
          <div>#</div>
          <div></div>
          <div>{t("controller.rundownEditor.item")}</div>
          <div>{t("controller.rundownEditor.description")}</div>
          <div>{t("controller.rundownEditor.planTime")}</div>
          <div>{t("controller.rundownEditor.duration")}</div>
          <div>{t("controller.rundownEditor.actions")}</div>
        </div>
      </div>

      {/* MIDDLE SECTION: Scrollable List */}
      <div
        data-testid="rundown-items-list"
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "10px var(--spacing-lg)",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          minHeight: 0,
        }}
      >
        {rundown.map((item, index) => {
          const showParallel = item.isParallel || rundown[index + 1]?.isParallel;
          const isEventLive = item.status === "live" && systemActiveId === selectedRundownId;

          return (
            <RundownItemRow
              key={item.id}
              item={item}
              index={index}
              isEditing={editingId === item.id}
              isLive={isEventLive}
              isDragging={draggedItemIndex === index}
              showParallel={!!showParallel}
              editForm={editForm}
              onEditFormChange={setEditForm}
              onStartEditing={startEditing}
              onSaveEdit={saveEdit}
              onCancelEdit={cancelEdit}
              onDelete={handleDelete}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            />
          );
        })}
      </div>

      {/* BOTTOM SECTION: Add Block Panel */}
      <AddEventPanel
        onAddItem={({ title, description, duration, plannedTime, isParallel }) => {
          let calculatedTime = plannedTime;
          if (!calculatedTime && rundown.length > 0) {
            const last = rundown[rundown.length - 1];
            calculatedTime = addTimes(last.plannedTime || "00:00:00", last.duration);
          } else if (!calculatedTime) {
            calculatedTime = "00:00:00";
          }
          const newItem: RundownItem = {
            id: `RD_${Date.now()}`,
            title,
            description,
            duration,
            plannedTime: calculatedTime,
            status: "future",
            isParallel,
          };
          const newList = [...rundown, newItem];
          const recalculatedList = recalculateRundownTimes(newList);
          updateRundown(recalculatedList);
        }}
      />

      <CustomDialog
        isOpen={dialog.isOpen}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        onConfirm={dialog.onConfirm}
        onCancel={() => setDialog((d) => ({ ...d, isOpen: false }))}
        defaultValue={dialog.defaultValue}
      />
    </div>
  );
};
